import * as React from 'react';
import styled from 'styled-components';
import {
  getCountryByIP,
  getCurrencyByCountryCode,
  postMessageToIosWrapperWithResponse
} from '../../utils';
import { getStoreState } from '../../state/Store';
import * as Config from '../../data/Config';
import Plan, { getPaidPlanFromPeriod } from '../Plan';
import { AppStorePlan, Order } from '../Order';
import { OverlayOption, OverlayOptions } from '../../components/OverlayOptions';
import Subtitle from './Subtitle';
import { useUser } from 'hooks/useUser';
import { useMobileLayout } from 'hooks/useMobileLayout';
import { PlatformType } from 'data/PlatformType';

const Body = styled.div`
  padding-top: 16px;
  line-height: 25px;
  display: flex;
  flex-direction: column;
  justify-content: start;
  font-size: 15px;
  line-height: 22px;
  flex-grow: 1;
  width: 100%;

  ul {
    text-align: left;

    li:before {
      content: '✓ ';
    }
    li.notavailable {
      opacity: 0.5;
      &:before {
        content: '𐄂 ';
      }
    }
  }
`;

const Cost = styled.span`
  font-size: 56px;
  line-height: 1;

  @media (max-width: 599px) {
    font-size: 40px;
  }
`;
const Unit = styled.span`
  font-size: 28px;

  @media (max-width: 599px) {
    font-size: 20px;
  }
`;

const Offer = styled.div`
  position: absolute;
  width: 100%;
  bottom: -22px;
  font-size: 13px;
`;

const Price = styled.div`
  margin: 20px 0;
  position: relative;

  @media (max-width: 599px) {
    margin: 16px 0;
  }
`;

type UnitType = 'day' | 'month' | 'year';
interface PlanBodyProps {
  amount?: number;
  currency?: string;
  amountWithCurrency?: string;
  unit: UnitType;
}

const PlanBody: React.FunctionComponent<PlanBodyProps> = ({
  amount,
  currency,
  amountWithCurrency,
  unit,
  children
}) => {
  return (
    <>
      {children && <Body>{children}</Body>}

      <Price>
        <Cost>
          <>
            {amountWithCurrency !== undefined
              ? amountWithCurrency
              : `${currency}${
                  (amount ?? 0) / (unit === 'year' ? 12 : 1)
                  /* (amount ?? 0) * (1 - (discount !== undefined ? discount : 0)) */
                }`}
          </>
        </Cost>
        <Unit>/{unit === 'year' ? 'month' : unit}</Unit>
        {unit === 'year' && (
          <Offer>
            Billed <strong>${amount}/year</strong>
          </Offer>
        )}
        {/* {discount !== undefined && (
          <Offer>
            <span>
              Was {currency}
              {amount} -
            </span>{' '}
            Save {discount * 100}%
          </Offer>
        )} */}
      </Price>
    </>
  );
};

const createOptionFromAppStorePlan = (plan: AppStorePlan): OverlayOption => {
  const tAndC = (
    <>
      {' '}
      <a
        href="https://www.beatflyer.com/privacy.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        Privacy Policy
      </a>{' '}
      and{' '}
      <a
        href="https://www.beatflyer.com/terms.html"
        target="_blank"
        rel="noopener noreferrer"
      >
        Terms & Conditions
      </a>
      .
    </>
  );
  return {
    key: plan.period,
    title: plan.name,
    cta: plan.isSubscription ? 'Subscribe' : 'Buy',
    planDetails: (
      <PlanBody amountWithCurrency={plan.price} unit={plan.period} />
    ),
    footer: plan.isSubscription ? (
      <div>
        Cancel any time. Subscription auto-renews.
        <br />
        {tAndC}
      </div>
    ) : undefined
  };
};

const detectCurrency = async (): Promise<string> => {
  const countryCode = await getCountryByIP();
  return await getCurrencyByCountryCode(countryCode);
};

interface Props {
  onSelect: (orderOrStorePlan: Order | AppStorePlan) => void;
}

export const PlanSelection: React.FC<Props> = ({ onSelect }) => {
  const user = useUser();
  const plan = user?.plan ?? Plan.Free;
  const [currency, setCurrency] = React.useState(user?.currency ?? '');
  const isMobileApp = useMobileLayout();
  const isIosApp = getStoreState().app.platform === PlatformType.iOSApp;
  const defaultItems: OverlayOption[] = [];
  const [wrapperItems, setWrapperItems] = React.useState<OverlayOption[]>([]);
  const [appStorePlans, setAppStorePlans] = React.useState<AppStorePlan[]>([]);
  // let monthlyDiscount: number | undefined = undefined;
  // if (this.state.offer) {
  //   // TODO: this should be on the server
  //   monthlyDiscount = parseFloat(this.state.offer as string) * 0.01;
  // }
  const footer = 'Local taxes not included';
  let isPro = plan === Plan.ProMonthly || plan === Plan.ProYearly;
  // if (user?.customerId !== undefined) {
  defaultItems.push({
    key: 'day',
    title: 'Day pass',
    cta: isPro ? 'Already Pro' : 'Buy',
    planDetails: (
      <PlanBody amount={Config.COST_PER_DAY} currency={currency} unit="day">
        {isMobileApp ? undefined : (
          <ul>
            <li>No watermark</li>
            <li>Access all templates</li>
            <li>Remove background from photos</li>
            <li className="notavailable">Save projects in the cloud</li>
            <li className="notavailable">Save brand color themes</li>
            <li className="notavailable">Use custom fonts</li>
          </ul>
        )}
      </PlanBody>
    ),
    footer: isMobileApp ? undefined : footer,
    disabled: isPro
  });
  /*} else {
        items.push({
          key: 'free',
          title: 'Free',
          description: 'Try the product or casual use',
          cta: 'Always available',
          planDetails: (
            <PlanBody amount={0} currency={currency} unit="∞">
              Create unlimited videos with watermark
              <br />
              Access some templates
            </PlanBody>
          ),
          disabled: true
        });
      }*/
  const allFeatures = (
    <ul>
      <li>No watermark</li>
      <li>Access all templates</li>
      <li>Remove background from photos</li>
      <li>Save projects in the cloud</li>
      <li>Save brand color themes</li>
      <li>Use custom fonts</li>
    </ul>
  );
  defaultItems.push({
    key: 'year',
    title: 'Yearly subscription',
    cta: isPro ? 'Already Pro' : 'Subscribe',
    planDetails: (
      <PlanBody
        amount={Config.COST_PER_YEAR}
        currency={currency}
        unit="year"
        // discount={monthlyDiscount}
      >
        {isMobileApp ? undefined : allFeatures}
      </PlanBody>
    ),
    footer: isMobileApp ? undefined : `Cancel anytime. ${footer}`,
    disabled: plan === Plan.ProYearly,
    bannerUrl: '/media/save-50-banner.svg'
  });
  defaultItems.push({
    key: 'month',
    title: 'Monthly subscription',
    cta: isPro ? 'Already Pro' : 'Subscribe',
    planDetails: (
      <PlanBody
        amount={Config.COST_PER_MONTH}
        currency={currency}
        unit="month"
        // discount={monthlyDiscount}
      >
        {isMobileApp ? undefined : allFeatures}
      </PlanBody>
    ),
    footer: isMobileApp ? undefined : `Cancel anytime. ${footer}`,
    disabled: isPro
    // bannerUrl: isMobileApp ? undefined : '/media/most-popular-banner.svg'
  });

  const didSelectPlan = React.useCallback(
    (key: string) => {
      if (isIosApp) {
        const selectedPlan = appStorePlans.find(p => p.period === key);
        if (selectedPlan) {
          onSelect(selectedPlan);
        }
        return;
      }
      switch (key) {
        case 'day':
          let locationState = getStoreState().app.history.location.state;
          if (locationState && locationState.intent === 'save') {
            const result = confirm(
              "Day passes won't allow you to save flyers. You will need either a monthly or a yearly subscription. Continue?"
            );
            if (!result) {
              return;
            }
          }
          onSelect({
            plan: Plan.ProDay,
            cost: Config.COST_PER_DAY
          });
          break;
        case 'month':
          onSelect({
            plan: Plan.ProMonthly,
            cost: Config.COST_PER_MONTH
          });
          break;
        case 'year':
          onSelect({
            plan: Plan.ProYearly,
            cost: Config.COST_PER_YEAR
          });
          break;
        default:
          break;
      }
    },
    [appStorePlans, onSelect]
  );

  React.useEffect(() => {
    let mounted = true;
    if (currency === '') {
      detectCurrency().then(value => {
        if (mounted) {
          setCurrency(value);
        }
      });
    }
    if (isIosApp) {
      postMessageToIosWrapperWithResponse('requestPlans').then(
        (plans: AppStorePlan[]) => {
          plans.forEach(p => {
            p.plan = getPaidPlanFromPeriod(p.period);
          });
          console.log(plans);
          if (mounted) {
            setAppStorePlans(plans);
            setWrapperItems(plans.map(createOptionFromAppStorePlan));
          }
        }
      );
    } else {
      const urlParams = new URLSearchParams(window.location.search);
      const duration = urlParams.get('duration');
      if (duration && ['day', 'month', 'year'].indexOf(duration) > -1) {
        didSelectPlan(duration);
      }
    }
    return () => {
      mounted = false;
    };
  }, []);

  const items = isIosApp ? wrapperItems : defaultItems;
  const isLoading = currency.length === 0 || items.length === 0;
  return isLoading ? (
    <h1>Loading...</h1>
  ) : (
    <>
      <header>
        {isMobileApp ? (
          <h1>Unlock all features and templates</h1>
        ) : (
          <>
            {' '}
            <h1>Upgrade to unlock all features</h1>
            <Subtitle>
              Remove the watermark, access all templates, save your projects in
              the cloud and much more.
            </Subtitle>
          </>
        )}
      </header>
      <OverlayOptions items={items} onSelect={didSelectPlan} />
    </>
  );
};



// WEBPACK FOOTER //
// ./src/buy/components/PlansView.tsx